import React from 'react'
import { Text, Link } from '@/components/lib'
import { split } from '@/utils/mentions'

const URL_PATTERN = /(https?:\/\/[^\s]+)/g

/**
 * Free text with its @-mentions shown as names rather than the markup they are
 * stored as (see `utils/mentions.js`). Anything that displays a query comment
 * or a participant note has to render through this, or the reader is shown the
 * raw `@[Name](user:...)` token.
 *
 * Web addresses in the surrounding words become links, as they do on the site
 * message boards — people paste them into comments constantly.
 */
export default function MentionText ({ text, currentUserId, ...textProps }) {
  const parts = split(text)
  if (!parts.length) return null
  return (
    <Text whiteSpace='pre-wrap' {...textProps}>
      {parts.map((part, i) => {
        if (part.type === 'mention') {
          // Your own name stands out from the others, so a long thread shows at
          // a glance where you were the one being asked.
          const isYou = currentUserId && part.id === String(currentUserId).toLowerCase()
          return (
            <Text
              as='span'
              key={i}
              px={1}
              rounded='sm'
              fontWeight='semibold'
              whiteSpace='nowrap'
              bg={isYou ? 'primary.solid' : 'primary.subtle'}
              color={isYou ? 'fg.onAccent' : 'primary.fg'}
            >
              @{part.name}
            </Text>
          )
        }
        return withLinks(part.value, i)
      })}
    </Text>
  )
}

function withLinks (value, keyPrefix) {
  const pieces = []
  const pattern = new RegExp(URL_PATTERN.source, 'g')
  let lastIndex = 0
  let match
  while ((match = pattern.exec(value)) !== null) {
    if (match.index > lastIndex) pieces.push(value.substring(lastIndex, match.index))
    pieces.push(
      <Link
        key={`${keyPrefix}-${match.index}`}
        href={match[0]}
        target='_blank'
        rel='noopener noreferrer'
        textDecoration='underline'
      >
        {match[0]}
      </Link>
    )
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < value.length) pieces.push(value.substring(lastIndex))
  return <React.Fragment key={keyPrefix}>{pieces}</React.Fragment>
}
